import React from 'react';

import { useClaim } from '~/components/ClaimContainer';
import AddressVerificationBadge from '~/components/Contacts/AddressVerificationBadge/AddressVerificationBadge';
import BusinessVerificationBadge from '~/components/Contacts/BusinessVerificationBadge/BusinessVerificationBadge';
import { areActionsEnabled } from '~/components/Contacts/contactActionsUtils';
import Address from '~/components/Contacts/ContactMiniCard/Details/Address';
import BusinessNumber from '~/components/Contacts/ContactMiniCard/Details/BusinessNumber';
import COI from '~/components/Contacts/ContactMiniCard/Details/COI';
import ContractOfService from '~/components/Contacts/ContactMiniCard/Details/ContractOfService';
import DyteVideo from '~/components/Contacts/ContactMiniCard/Details/DyteVideo';
import Emails from '~/components/Contacts/ContactMiniCard/Details/Emails';
import Exposures from '~/components/Contacts/ContactMiniCard/Details/Exposures';
import Note from '~/components/Contacts/ContactMiniCard/Details/Note';
import Phones from '~/components/Contacts/ContactMiniCard/Details/Phones';
import W9 from '~/components/Contacts/ContactMiniCard/Details/W9';
import type { ContactDetailsPropsType } from '~/components/Contacts/ContactMiniCard/types';
import {
  isGbgSupportedCountry,
  useGbgValidationStatus,
} from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/useGbgValidationStatus';
import { useCms } from '~/components/hooks/useCms';
import Divider from '~/components/ui/atoms/Divider';

const ContactDetails: React.FC<ContactDetailsPropsType> = ({ contact, onUpdate }) => {
  const { user } = useCms();
  const { claim, onAsyncClaimUpdate } = useClaim();
  const gbgValidationStatus = useGbgValidationStatus();

  const actionsEnabled = areActionsEnabled(user, claim);

  const updateIfPossible = async () => {
    if (onUpdate) {
      await onUpdate();
    } else if (claim && onAsyncClaimUpdate) {
      await onAsyncClaimUpdate();
    }
  };

  const shouldShowAddressBadge =
    gbgValidationStatus.is_address_validation_enabled && isGbgSupportedCountry(contact.country);
  const shouldShowBusinessSection =
    gbgValidationStatus.is_business_validation_enabled && isGbgSupportedCountry(contact.country) && contact.is_company;

  return (
    <div>
      {!contact.is_deactivated && (
        <>
          <Address contact={contact} onUpdate={updateIfPossible} actionsEnabled={actionsEnabled} />
          <Divider className="my-12" />
          {shouldShowAddressBadge ? (
            <AddressVerificationBadge validations={contact.validations} className="-mt-8 mb-12" />
          ) : null}
          {shouldShowBusinessSection ? (
            <>
              <BusinessNumber contact={contact} onUpdate={updateIfPossible} actionsEnabled={actionsEnabled} />
              <Divider className="my-12" />
              {contact.au_nz_business_number ? (
                <BusinessVerificationBadge
                  validations={contact.validations}
                  country={contact.country}
                  className="-mt-8 mb-12"
                />
              ) : null}
            </>
          ) : null}
          <Emails contact={contact} onUpdate={updateIfPossible} actionsEnabled={actionsEnabled} />
          <Divider className="my-12" />
          <Phones contact={contact} onUpdate={updateIfPossible} actionsEnabled={actionsEnabled} />
          <DyteVideo contact={contact} onUpdate={updateIfPossible} actionsEnabled={actionsEnabled} />
        </>
      )}
      <Exposures contact={contact} />
      <Divider className="my-12" />
      <Note contact={contact} />
      <W9 contact={contact} />
      <COI contact={contact} />
      <ContractOfService contact={contact} />
    </div>
  );
};

export default ContactDetails;
